import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ProtectedRoute({ children, roles }) {
  const {
    user,
    loading,
    landingPath,
    mustChangePassword,
  } = useAuth();

  const location = useLocation();

  /*
   * ------------------------------------------------------------------------
   * Authentication loading
   * ------------------------------------------------------------------------
   */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div
          className="text-muted-foreground text-sm font-mono"
          data-testid="protected-loading"
        >
          Loading workspace…
        </div>
      </div>
    );
  }

  /*
   * ------------------------------------------------------------------------
   * Not authenticated
   * ------------------------------------------------------------------------
   */

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: location.pathname,
        }}
      />
    );
  }

  /*
   * ------------------------------------------------------------------------
   * Mandatory password change
   * ------------------------------------------------------------------------
   *
   * A user with a system-assigned password must not be able to bypass the
   * requirement simply by typing another application URL.
   *
   * Login.jsx handles the actual password-change interface.
   * ------------------------------------------------------------------------
   */

  if (
    mustChangePassword &&
    location.pathname !== "/login"
  ) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          forcePasswordChange: true,
        }}
      />
    );
  }

  /*
   * ------------------------------------------------------------------------
   * Super administrator
   * ------------------------------------------------------------------------
   *
   * Super admin bypasses ordinary role restrictions.
   * ------------------------------------------------------------------------
   */

  if (user.role === "super_admin") {
    return children;
  }

  /*
   * ------------------------------------------------------------------------
   * Route has no role restriction
   * ------------------------------------------------------------------------
   */

  if (!roles || roles.length === 0) {
    return children;
  }

  /*
   * ------------------------------------------------------------------------
   * Check role authorization
   * ------------------------------------------------------------------------
   */

  const allowed = roles.includes(user.role);

  if (allowed) {
    return children;
  }

  /*
   * ------------------------------------------------------------------------
   * Unauthorized
   * ------------------------------------------------------------------------
   *
   * NEVER redirect unauthorized users to /dashboard.
   *
   * Use the same landingPath returned by server.py.
   * ------------------------------------------------------------------------
   */

  const destination =
    landingPath || "/dashboard";

  /*
   * ------------------------------------------------------------------------
   * Prevent redirect loops
   * ------------------------------------------------------------------------
   */

  if (destination === location.pathname) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-sm font-semibold">
            Access restricted
          </div>

          <div className="text-xs text-muted-foreground mt-1">
            This page is not available for your role.
          </div>
        </div>
      </div>
    );
  }

  return (
    <Navigate
      to={destination}
      replace
      state={{
        unauthorizedFrom: location.pathname,
      }}
    />
  );
}